import React, { useMemo } from "react";
import {
  Typography,
  Stack,
  Divider,
  Tooltip,
  Skeleton,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PaymentIcon from "@mui/icons-material/Payment";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningIcon from "@mui/icons-material/Warning";
import { calculateFees } from "@/lib/helpers/feeCalculator";
import { useTimezone } from "@/app/utils/TimezoneContext";
import { getRescheduleInfo } from "@/lib/helpers/meetingCancelRescheduleHelpers";
import moment from "moment-timezone";

const ScheduleModalSidebar = ({
  meetingData,
  displayTimezone,
  isMobile,
  feeConfig,
  estimatedTax,
  billingPreview,
  isRescheduleMode = false,
}) => {
  const { timezone } = useTimezone();

  const fees = useMemo(() => {
    if (!feeConfig) return { seekerServiceFee: 0, seekerTotalFeeDue: 0 };
    return calculateFees(meetingData?.session?.session_rate, feeConfig);
  }, [feeConfig, meetingData]);

  const rateAdjustedBySharer = meetingData?.session?.rate_adjusted_by_sharer;
  const originalSessionRate = meetingData?.session?.original_session_rate;
  const sessionRate = meetingData?.session?.session_rate;
  const feeLineFontSize = isMobile ? "15px" : "13px";
  const effectiveServiceFee =
    billingPreview?.serviceFee ?? fees.seekerServiceFee ?? 0;
  const effectiveTax = billingPreview?.taxAmount ?? estimatedTax ?? 0;
  const waivedTotal = billingPreview?.waivedTotalAmount || 0;
  const displayServiceFee =
    waivedTotal > 0
      ? (billingPreview?.originalServiceFee ?? effectiveServiceFee)
      : effectiveServiceFee;
  const displayTax =
    waivedTotal > 0
      ? (billingPreview?.originalTaxAmount ?? effectiveTax)
      : effectiveTax;
  const effectiveTotal =
    billingPreview?.totalAmount ?? fees.seekerTotalFeeDue + (estimatedTax || 0);

  // Get reschedule information
  const rescheduleInfo = useMemo(() => {
    if (!isRescheduleMode) return null;
    return getRescheduleInfo(meetingData);
  }, [isRescheduleMode, meetingData]);

  const getCurrentMeetingTime = () => {
    if (!meetingData?.meeting_time) return "Not scheduled";

    try {
      // Convert Firestore timestamp to Date
      const time = meetingData.meeting_time.toDate
        ? meetingData.meeting_time.toDate()
        : new Date(meetingData.meeting_time);

      // Format in user's timezone with friendly format
      const formatted = moment(time)
        .tz(timezone)
        .format("dddd, MMM Do, YYYY [at] h:mm A");

      return formatted;
    } catch (error) {
      console.error("Error formatting meeting time:", error);
      return "Invalid date";
    }
  };

  return (
    <Box sx={{ mt: 0 }}>
      {!isMobile && (
        <Typography variant="h1b" sx={{ fontWeight: 700 }}>
          {isRescheduleMode ? "Reschedule Meeting" : "Schedule Meeting"}
        </Typography>
      )}
      {isMobile && <Divider sx={{ mb: 2 }} />}
      {/* Meeting Duration */}
      {!isMobile && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mt: isMobile ? 2 : 4,
            mb: 0.5,
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontSize: "13px" }}>
            {meetingData?.session?.duration} minutes
          </Typography>
        </Stack>
      )}
      {/* Timezone */}
      {!isMobile && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mb: isMobile ? 3 : 4,
          }}
        >
          <LanguageIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontSize: "13px" }}>
            {displayTimezone}
          </Typography>
        </Stack>
      )}
      {/* Current Meeting Time (for reschedule mode) */}
      {isRescheduleMode && (
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 1,
            }}
          >
            <EventAvailableIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Current Meeting Time:
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              ml: 3,
              mb: 0.5,
            }}
          >
            {getCurrentMeetingTime()}
          </Typography>
        </Box>
      )}
      {/* Reschedule Limit Info */}
      {isRescheduleMode && rescheduleInfo && (
        <>
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mb: 1,
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Reschedule Policy
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                ml: 3,
              }}
            >
              {rescheduleInfo.isFirstReschedule && (
                <>
                  This is your 1st reschedule, you can reschedule once more,
                  unless the new meeting time is less than 24 hours away.
                </>
              )}
              {rescheduleInfo.isFinalReschedule && (
                <>
                  This is your final reschedule. No more reschedules will be
                  available after this.
                </>
              )}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                mb: 1,
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Free Rescheduling
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                ml: 3,
              }}
            >
              No additional charges apply.
            </Typography>
          </Box>
        </>
      )}
      {/* Fee breakdown section - only show for initial scheduling */}
      {!isRescheduleMode && feeConfig && (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <AttachMoneyIcon sx={{ fontSize: 16 }} />
            {rateAdjustedBySharer && originalSessionRate != null ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: feeLineFontSize }}>
                  Session Fee
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: "line-through",
                    opacity: 0.55,
                    fontSize: feeLineFontSize,
                  }}
                >
                  ${originalSessionRate}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: feeLineFontSize }}
                >
                  {sessionRate === 0 ? "FREE" : `$${sessionRate}`}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontSize: feeLineFontSize }}>
                Session Fee ${sessionRate}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontSize: feeLineFontSize }}>
              Service Fee ${displayServiceFee.toFixed(2)}
            </Typography>
          </Stack>
          {estimatedTax === null &&
          meetingData?.session?.session_rate === 0 ? null : estimatedTax ===
            null ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <Skeleton variant="text" width={100} height={20} />
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontSize: feeLineFontSize }}>
                Tax on service fee ${displayTax.toFixed(2)}
              </Typography>
            </Stack>
          )}
          {waivedTotal > 0 && (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                mt: 0.25,
                pl: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: feeLineFontSize,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: 12, fontSize: 20, lineHeight: 1 }}>
                  -
                </span>
                <span>Special credit ${waivedTotal.toFixed(2)}</span>
              </Typography>
            </Stack>
          )}
          <Divider sx={{ my: 1.5 }} />
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: 24,
                mt: 0.25,
              }}
            >
              <PaymentIcon sx={{ fontSize: 18 }} />
            </Box>
            <Stack
              direction={{ xs: "row", sm: "row", md: "column" }}
              spacing={{ xs: 1, md: 0 }}
              sx={{
                alignItems: "center",
                flexGrow: 1,
              }}
            >
              <Typography variant="body2">Total Due Now</Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ${effectiveTotal.toFixed(2)} USD
                </Typography>
                <Box sx={{ display: { xs: "none", md: "flex" } }}>
                  <Tooltip
                    title="Once scheduled, the fee is charged to your credit card. If the meeting doesn't happen as planned, you'll receive a full refund."
                    arrow
                    placement="top"
                  >
                    <InfoIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
      {/* Session rate info for reschedule mode */}
      {isRescheduleMode && (
        <Box sx={{ mt: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 1,
            }}
          >
            <AttachMoneyIcon sx={{ fontSize: 16 }} />
            {rateAdjustedBySharer && originalSessionRate != null ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2">Session Fee:</Typography>
                <Typography
                  variant="body2"
                  sx={{ textDecoration: "line-through", opacity: 0.55 }}
                >
                  ${originalSessionRate}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sessionRate === 0 ? "FREE" : `$${sessionRate}`}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2">
                Session Fee: ${sessionRate}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleModalSidebar;
